import React, { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'
import { Pagination } from '../Pagination'
import { SearchBar } from '../SearchBar'
import { Stack } from 'components/Stack'
import { PopupMenu } from 'components/PopupMenu'
import { AreaTypeDetails } from 'components/AreaTypeDetails'
import { Project, ProjectTableFilters } from 'models/project'
import { DateUtils } from 'utils/date'
import styles from './ProjectsTable.module.scss'

type ProjectsTableProps = {
  totalItems: number
  projects: Project[]
  filters: ProjectTableFilters
  setFilters: (filters: ProjectTableFilters) => void
  deleteItem: (id: string) => void
  duplicateItem: (id: string) => void
}

export const ProjectsTable: FC<ProjectsTableProps> = ({ projects, ...props }) => {
  const { t } = useTranslation()
  const [activeSort, setActiveSort] = useState('name')

  const handleSorting = (attr: string, criteria: string) => {
    setActiveSort(attr)
    props.setFilters({ ...props.filters, sortBy: `${attr}_${criteria}` })
  }

  return (
    <Stack space="medium">
      <Pagination
        totalItems={props.totalItems}
        offset={props.filters.offset}
        limit={props.filters.limit}
        setFilters={(filter) => props.setFilters({ ...props.filters, ...filter })}
      />
      <div className={styles.tableContainer}>
        <div className={styles.searchBarContainer}>
          <SearchBar
            q={props.filters.q}
            setFilters={(q) => props.setFilters({ ...props.filters, q })}
          />
        </div>
        <table cellSpacing={0} className={styles.projectsTable} data-testid="projects-table">
          <thead>
            <tr>
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="name"
                label="project.table.name"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="areaType"
                label="project.table.projectType"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="city"
                label="project.table.city"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="municipality"
                label="project.table.municipality"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="state"
                label="project.table.state"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="country"
                label="project.table.country"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="updatedAt"
                label="project.table.updatedAt"
                activeSort={activeSort}
              />
              <TableHeaderCell
                handleSorting={handleSorting}
                attr="createdBy"
                label="project.table.createdBy"
                activeSort={activeSort}

              />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.id}>
                <td>
                  <Link to={`/projects/${project.id}`}>
                    <Text>{project.name}</Text>
                  </Link>
                </td>
                <td className={styles.areaTypeCell}>
                  <AreaTypeDetails project={project} />
                </td>
                <td>
                  <Text>{project.city || '-'}</Text>
                </td>
                <td>
                  <Text>{project.municipality || '-'}</Text>
                </td>
                <td>
                  <Text>{project.state || '-'}</Text>
                </td>
                <td>
                  <Text>{project.country || '-'}</Text>
                </td>
                <td>
                  <Text>{DateUtils.short(project.updatedAt)}</Text>
                </td>
                <td>
                  <Text>{project.owner.email}</Text>
                </td>
                <td>
                  <PopupMenu
                    label={t('project.table.action.label')}
                    trigger={<Icon name="more-fill" size="xl" />}
                    className={styles.actionsMenu}
                  >
                    <button onClick={() => props.duplicateItem(project.id)}>
                      <Text align="center" size="small" weight="strong">
                        {t('project.table.action.duplicate')}
                      </Text>
                    </button>
                    <button onClick={() => props.deleteItem(project.id)}>
                      <Text align="center" size="small" weight="strong">
                        {t('project.table.action.delete')}
                      </Text>
                    </button>
                  </PopupMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Stack>
  )
}

type TableHeaderCell = {
  attr: string
  label: string
  sort?: boolean
  activeSort: string
  handleSorting: (attr: string, criteria: string) => void
}

const TableHeaderCell: FC<TableHeaderCell> = ({ sort = true, ...props }) => {
  const { t } = useTranslation()
  const [sortCriteria, setSortCriteria] = useState('desc')

  const handleClick = () => {
    const newCriteria = sortCriteria === 'asc' ? 'desc' : 'asc'
    setSortCriteria(newCriteria)
    props.handleSorting(props.attr, sortCriteria)
  }

  useEffect(() => {
    if (props.activeSort !== props.attr) {
      setSortCriteria('asc')
    }
  }, [props.activeSort, props.attr])

  return (
    <th className={styles.th}>
      <button className={styles.thContainer} onClick={() => handleClick()}>
        <Text weight="strong">{t(props.label)}</Text>
        {sort && (
          <Icon
            label={t('project.table.sort', {
              property: t(props.label),
              criteria: t(`project.table.${sortCriteria}`),
            })}
            name={sortCriteria === 'asc' ? 'arrow-drop-down-line' : 'arrow-drop-up-line'}
            size="xl"
          />
        )}
      </button>
    </th>
  )
}
